
import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import { Puzzle, ReportedPuzzle, PuzzleContextType, Comment, Reaction, Timestamp } from './types';
import { useAuth } from './AuthContext';
import { db, Timestamp as TimestampValue, FieldValue, arrayUnion, arrayRemove } from './firebase';
import { parseHashtags, parseMentions } from './utils';
import { sendDirectNotification } from './notificationUtils';

const PuzzleContext = createContext<PuzzleContextType | undefined>(undefined);

export const usePuzzles = () => {
    const context = useContext(PuzzleContext);
    if (!context) {
        throw new Error('usePuzzles must be used within a PuzzleProvider');
    }
    return context;
};

export const PuzzleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, userProfile, allUsers } = useAuth();
    const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
    const [puzzlesLoading, setPuzzlesLoading] = useState(true);
    const [reportedPuzzles, setReportedPuzzles] = useState<ReportedPuzzle[]>([]);

    useEffect(() => {
        if (!user || !db) {
             setPuzzles([]);
             setPuzzlesLoading(false);
             return;
        }

        const unsubscribe = db.collection('puzzles')
            .orderBy('createdAt', 'desc')
            .onSnapshot((snapshot: any) => {
                const loadedPuzzles = snapshot.docs.map((doc: any) => ({
                    id: doc.id,
                    ...doc.data()
                })) as Puzzle[];
                setPuzzles(loadedPuzzles);
                setPuzzlesLoading(false);
            }, (error: any) => {
                if (error.code === 'permission-denied') {
                    console.warn("Puzzle fetch permission denied (likely not logged in).");
                } else {
                    console.error("Error fetching puzzles:", error);
                }
                setPuzzlesLoading(false);
            });

        return () => {
            unsubscribe();
        };
    }, [user]);

    useEffect(() => {
        if (!userProfile?.isAdmin || !db) {
            setReportedPuzzles([]);
            return;
        }

        const unsubscribeReports = db.collection('reported_puzzles')
            .orderBy('reportedAt', 'desc')
            .onSnapshot((snapshot: any) => {
                const reports = snapshot.docs.map((doc: any) => ({
                    reportId: doc.id,
                    ...doc.data()
                })) as ReportedPuzzle[];
                setReportedPuzzles(reports);
            }, (error: any) => {
                console.error("Error fetching reported puzzles:", error);
            });

        return () => unsubscribeReports();
    }, [userProfile?.isAdmin]);

    const savePuzzle = async (puzzleData: Omit<Puzzle, 'id' | 'createdAt' | 'creatorId' | 'creatorProfile'>, puzzleId?: string): Promise<string | undefined> => {
        if (!userProfile) {
            console.error("You must be logged in to post.");
            throw new Error("Not logged in");
        }
        
        const hashtags = parseHashtags(puzzleData.clue);
        const mentions = parseMentions(puzzleData.clue, allUsers);

        try {
            if (!db) throw new Error("Firebase not loaded");
            if (puzzleId) {
                await db.collection('puzzles').doc(puzzleId).update({
                    ...puzzleData,
                    hashtags,
                });
                return puzzleId;
            } else {
                const newPuzzle: Omit<Puzzle, 'id'> = {
                    ...puzzleData,
                    creatorId: userProfile.uid,
                    creatorProfile: {
                        displayName: userProfile.displayName,
                        photoURL: userProfile.photoURL,
                        postColor: userProfile.postColor,
                    },
                    createdAt: TimestampValue.now(),
                    likes: [],
                    dislikes: [],
                    hearts: [],
                    comments: [],
                    correctGuessers: [],
                    incorrectGuessers: [],
                    hashtags,
                };
                const newDocRef = await db.collection('puzzles').add(newPuzzle);
                
                mentions.forEach(mention => {
                    if (mention.uid !== userProfile.uid) {
                        sendDirectNotification(mention.uid, {
                            type: 'mention_in_post',
                            text: `mentioned you in a new puzzle: "${puzzleData.clue.substring(0, 30)}..."`,
                            fromUser: {
                                id: userProfile.uid,
                                name: userProfile.displayName,
                                avatar: userProfile.photoURL,
                            },
                            puzzle: {
                                id: newDocRef.id,
                                clue: puzzleData.clue,
                                imageUrl: puzzleData.imageUrl
                            }
                        });
                    }
                });

                const mutualFriends = allUsers.filter(u => 
                    userProfile.following.includes(u.uid) && 
                    u.following.includes(userProfile.uid) 
                );

                if (mutualFriends.length > 0) {
                    mutualFriends.forEach(friend => {
                        sendDirectNotification(friend.uid, {
                            type: 'new_post_friend',
                            text: `posted a new puzzle!`,
                            fromUser: {
                                id: userProfile.uid,
                                name: userProfile.displayName,
                                avatar: userProfile.photoURL,
                            },
                            puzzle: {
                                id: newDocRef.id,
                                clue: puzzleData.clue,
                                imageUrl: puzzleData.imageUrl
                            }
                        });
                    });
                }

                return newDocRef.id;
            }
        } catch (error: any) {
            console.error("Error saving puzzle:", error);
            throw error;
        }
    };
    
    const deletePuzzle = async (puzzleId: string) => {
        try {
            await db.collection('puzzles').doc(puzzleId).delete();
        } catch (error) {
            console.error("Error deleting puzzle:", error);
        }
    };

    const reportPuzzle = async (puzzleId: string, reason: string, details: string) => {
        const puzzleToReport = puzzles.find(p => p.id === puzzleId);
        if (!puzzleToReport) return;

        try {
            const newReport: Omit<ReportedPuzzle, 'reportId'> = {
                puzzle: puzzleToReport,
                reason,
                details,
                reportedAt: TimestampValue.now(),
            };
            await db.collection('reported_puzzles').add(newReport);

            sendDirectNotification(puzzleToReport.creatorId, {
                type: 'post_under_review',
                text: `Your post "${puzzleToReport.clue.substring(0, 30)}..." has been flagged and is under review.`,
                puzzle: {
                    id: puzzleToReport.id,
                    clue: puzzleToReport.clue,
                    imageUrl: puzzleToReport.imageUrl
                }
            });

            await deletePuzzle(puzzleId);
        } catch (error) {
            console.error("Error reporting puzzle:", error);
        }
    };
    
    const updatePuzzleReactions = async (puzzleId: string, reactionType: 'likes' | 'dislikes' | 'hearts', userId: string, isAdding: boolean) => {
        try {
            const puzzleRef = db.collection('puzzles').doc(puzzleId);
            if (isAdding) {
                await puzzleRef.update({
                    [reactionType]: arrayUnion(userId)
                });

                // Send notification to creator (except for own posts or dislikes)
                if (reactionType !== 'dislikes' && userProfile) {
                    const puzzle = puzzles.find(p => p.id === puzzleId);
                    if (puzzle && puzzle.creatorId !== userProfile.uid) {
                        const verb = reactionType === 'likes' ? 'liked' : 'loved';
                        sendDirectNotification(puzzle.creatorId, {
                            type: reactionType === 'likes' ? 'like' : 'heart',
                            text: `${verb} your puzzle: "${puzzle.clue.substring(0, 30)}..."`,
                            fromUser: {
                                id: userProfile.uid,
                                name: userProfile.displayName,
                                avatar: userProfile.photoURL,
                            },
                            puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl }
                        });
                    }
                }
            } else {
                await puzzleRef.update({
                    [reactionType]: arrayRemove(userId)
                });
            }
        } catch (error) {
            console.error("Error updating reaction:", error);
        }
    };
    
    const addCommentToPuzzle = async (puzzleId: string, comment: Omit<Puzzle['comments'][0], 'id'>) => {
        if (!userProfile) return;
        try {
            const mentions = parseMentions(comment.text || '', allUsers);
            const puzzleRef = db.collection('puzzles').doc(puzzleId);
            const commentWithId: any = { 
                ...comment, 
                id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                mentions,
            };
            
            Object.keys(commentWithId).forEach(key => {
                if (commentWithId[key] === undefined) {
                    delete commentWithId[key];
                }
            });
            
            await puzzleRef.update({
                comments: arrayUnion(commentWithId)
            });

            const puzzle = puzzles.find(p => p.id === puzzleId);
            
            mentions.forEach(mention => {
                if (mention.uid !== userProfile.uid) {
                    sendDirectNotification(mention.uid, {
                        type: 'mention_in_comment',
                        text: `mentioned you in a comment on "${puzzle?.clue.substring(0, 30)}..."`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL,
                        },
                        puzzle: puzzle ? { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl } : undefined,
                    });
                }
            });

            if (puzzle && puzzle.creatorId !== userProfile.uid) {
                // If this is a reply, we need to notify the original commenter
                if (comment.replyingToUserId && comment.replyingToUserId !== userProfile.uid) {
                    sendDirectNotification(comment.replyingToUserId, {
                        type: 'comment_reply',
                        text: `replied to your comment on "${puzzle.clue.substring(0, 30)}..."`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL,
                        },
                        puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl },
                    });
                }
                
                // If it's replying to a parent thread and also has a deep nested reply target, we only notify the direct deeply nested target above to prevent duplicate spam, unless the top level parent is different!
                if (comment.replyToId) {
                    const parentComment = puzzle.comments.find(c => c.id === comment.replyToId);
                    if (parentComment && parentComment.userId !== userProfile.uid && parentComment.userId !== comment.replyingToUserId) {
                        sendDirectNotification(parentComment.userId, {
                            type: 'comment_reply',
                            text: `replied in a thread on your comment on "${puzzle.clue.substring(0, 30)}..."`,
                            fromUser: {
                                id: userProfile.uid,
                                name: userProfile.displayName,
                                avatar: userProfile.photoURL,
                            },
                            puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl },
                        });
                    }
                }

                // Notify puzzle creator (skip if they are already getting a reply notification)
                if (puzzle.creatorId !== comment.replyingToUserId) {
                    sendDirectNotification(puzzle.creatorId, {
                        type: 'comment',
                        text: `commented on your puzzle: "${comment.text.substring(0, 30)}..."`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL,
                        },
                        puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl },
                    });
                }
            } else if (puzzle && comment.replyingToUserId) {
                // If it's my own puzzle but I'm replying to someone else
                if (comment.replyingToUserId !== userProfile.uid) {
                    sendDirectNotification(comment.replyingToUserId, {
                        type: 'comment_reply',
                        text: `replied to your comment on "${puzzle.clue.substring(0, 30)}..."`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL,
                        },
                        puzzle: { id: puzzle.id, clue: puzzle.clue, imageUrl: puzzle.imageUrl },
                    });
                }
            }

        } catch (error) {
            console.error("Error adding comment:", error);
        }
    };

    const deleteComment = async (puzzleId: string, commentId: string, currentComments: Comment[]) => {
        try {
            const newComments = currentComments.filter(c => c.id !== commentId);
            await db.collection('puzzles').doc(puzzleId).update({
                comments: newComments
            });
        } catch (error) {
            console.error("Error deleting comment:", error);
        }
    };

    const addReactionToComment = async (puzzleId: string, commentId: string, emoji: string) => {
        if (!userProfile) return;
        const puzzleRef = db.collection('puzzles').doc(puzzleId);

        try {
            await db.runTransaction(async (transaction: any) => {
                const puzzleDoc = await transaction.get(puzzleRef);
                if (!puzzleDoc.exists) return;

                const puzzleData = puzzleDoc.data() as Puzzle;
                const comments = [...(puzzleData.comments || [])];
                const commentIndex = comments.findIndex(c => c.id === commentId);

                if (commentIndex === -1) return;

                const comment = { ...comments[commentIndex] };
                const reactions: Reaction = comment.reactions || {};
                const reactors = reactions[emoji] || [];

                if (reactors.includes(userProfile.uid)) {
                    reactions[emoji] = reactors.filter(uid => uid !== userProfile.uid);
                    if (reactions[emoji].length === 0) {
                        delete reactions[emoji];
                    }
                } else {
                    reactions[emoji] = [...reactors, userProfile.uid];
                }
                
                comment.reactions = reactions;
                comments[commentIndex] = comment;

                transaction.update(puzzleRef, { comments });

                // Send notification to the comment author if someone else reacted
                if (!reactors.includes(userProfile.uid) && comment.userId !== userProfile.uid) {
                    sendDirectNotification(comment.userId, {
                        type: 'comment_reaction',
                        text: `reacted ${emoji} to your comment: "${comment.text.substring(0, 20)}..."`,
                        fromUser: {
                            id: userProfile.uid,
                            name: userProfile.displayName,
                            avatar: userProfile.photoURL,
                        },
                        puzzle: puzzleData ? { id: puzzleData.id, clue: puzzleData.clue, imageUrl: puzzleData.imageUrl } : undefined,
                    });
                }
            });
        } catch (error) {
            console.error("Error adding reaction to comment:", error);
        }
    };

    const reinstatePuzzle = async (reportId: string) => {
        const report = reportedPuzzles.find(r => r.reportId === reportId);
        if (report) {
            try {
                await db.collection('puzzles').doc(report.puzzle.id).set(report.puzzle);
                
                await sendDirectNotification(report.puzzle.creatorId, {
                    type: 'post_under_review',
                    text: `Good news! Your post "${report.puzzle.clue.substring(0, 30)}..." was reviewed and reinstated.`,
                    puzzle: {
                        id: report.puzzle.id,
                        clue: report.puzzle.clue,
                        imageUrl: report.puzzle.imageUrl
                    }
                });

                await db.collection('reported_puzzles').doc(reportId).delete();
            } catch (error) {
                console.error("Error reinstating puzzle:", error);
            }
        }
    };

    const deleteReportedPuzzle = async (reportId: string) => {
        const report = reportedPuzzles.find(r => r.reportId === reportId);
        if (report) {
            try {
                await sendDirectNotification(report.puzzle.creatorId, {
                    type: 'post_under_review',
                    text: `Your post "${report.puzzle.clue.substring(0, 30)}..." was removed. Reason: ${report.reason}`,
                });

                await db.collection('reported_puzzles').doc(reportId).delete();
            } catch (error) {
                console.error("Error deleting report:", error);
            }
        }
    };

    const refreshPuzzles = useCallback(async () => {}, []);
    
    const recordPlayerScore = async (puzzleId: string, userId: string, score: number, completedAt: Timestamp) => {
        try {
            const puzzleRef = db.collection('puzzles').doc(puzzleId);
            await puzzleRef.update({
                [`playerScores.${userId}`]: { score, completedAt }
            });
        } catch (error) {
            console.error("Error recording timed score:", error);
        }
    };

    const sharePuzzleToFeed = (puzzleId: string) => {
         console.log(`Sharing puzzle ${puzzleId} to feed.`);
    };

    const repostPuzzle = async (puzzleId: string) => {
        const originalPuzzle = puzzles.find(p => p.id === puzzleId);
        if (originalPuzzle && userProfile) {
            try {
                const { id, ...data } = originalPuzzle;
                const newPuzzle = {
                    ...data,
                    creatorId: userProfile.uid,
                    creatorProfile: {
                         displayName: userProfile.displayName,
                         photoURL: userProfile.photoURL,
                    },
                    createdAt: TimestampValue.now(),
                    likes: [],
                    dislikes: [],
                    hearts: [],
                    comments: [],
                    clue: `RT: ${data.clue}`
                };
                await db.collection('puzzles').add(newPuzzle);
            } catch (error) {
                console.error("Reposting:", error);
            }
        }
    };

    const recordGuessAttempt = async (puzzleId: string, userId: string, isCorrect: boolean, guess?: string) => {
        console.log(`PuzzleContext: recordGuessAttempt called for puzzle ${puzzleId}, user ${userId}, isCorrect: ${isCorrect}`);
        try {
            const puzzleRef = db.collection('puzzles').doc(puzzleId);
            const fieldToUpdate = isCorrect ? 'correctGuessers' : 'incorrectGuessers';
            const updates: any = {
                [fieldToUpdate]: arrayUnion(userId)
            };
            if (!isCorrect && guess) {
                updates.incorrectGuesses = arrayUnion({
                    userId,
                    guess,
                    timestamp: Date.now()
                });
            }
            await puzzleRef.update(updates);
            console.log(`PuzzleContext: recordGuessAttempt successful for puzzle ${puzzleId}, field: ${fieldToUpdate}`);
        } catch (error) {
            console.error("PuzzleContext: Error recording guess attempt:", error);
        }
    };

    const value: PuzzleContextType = {
        puzzles,
        puzzlesLoading,
        reportedPuzzles,
        savePuzzle,
        deletePuzzle,
        reportPuzzle,
        reinstatePuzzle,
        deleteReportedPuzzle,
        refreshPuzzles,
        recordPlayerScore,
        sharePuzzleToFeed,
        repostPuzzle,
        updatePuzzleReactions,
        addCommentToPuzzle,
        deleteComment,
        recordGuessAttempt,
        addReactionToComment,
    };

    return <PuzzleContext.Provider value={value}>{children}</PuzzleContext.Provider>;
};
